/**
 * Payable Offline Service
 *
 * Extends BaseOfflineService for accounts payable operations.
 */

import type { Table } from 'dexie';

import { offlineDb } from '../database/offlineDatabase';
import { BaseOfflineService } from './BaseOfflineService';
import type {
  EntityType,
  OfflineEntity,
  ApiServiceMethods,
} from '../types/offline.types';

// Import payable types
import type {
  AccountPayable,
  CreatePayableDto,
  UpdatePayableDto,
  QueryPayablesDto,
  PayablesSummary,
} from '@/types/payables.types';
import { DebtStatus } from '@/types/enum';

// Import online service
import * as payableService from '@/api/services/payableService';

// ============================================
// OFFLINE SERVICE
// ============================================

export class PayableOfflineService extends BaseOfflineService<
  AccountPayable,
  CreatePayableDto,
  UpdatePayableDto
> {
  protected readonly entityType: EntityType = 'payable';
  protected readonly table: Table<OfflineEntity<AccountPayable>, string> = offlineDb.payables;

  protected readonly apiService: ApiServiceMethods<
    AccountPayable,
    CreatePayableDto,
    UpdatePayableDto
  > = {
    getAll: (filters) => payableService.getAll(filters as QueryPayablesDto),
    getOne: payableService.getById,
    create: payableService.create,
    update: payableService.update,
    delete: payableService.remove,
  };

  // ============================================
  // PAYABLE-SPECIFIC METHODS
  // ============================================

  /**
   * Get payables by status
   */
  async getByStatus(status: DebtStatus): Promise<AccountPayable[]> {
    const payables = await this.table.where('status').equals(status).toArray();
    return payables.map(this.stripOfflineMetadata);
  }

  /**
   * Get pending payables (PENDING or PARTIAL)
   */
  async getPending(): Promise<AccountPayable[]> {
    const payables = await this.table
      .filter((p) => p.status === DebtStatus.ACTIVE || p.status === DebtStatus.PARTIAL)
      .toArray();
    return payables.map(this.stripOfflineMetadata);
  }

  /**
   * Get payables by contact
   */
  async getByContact(contactId: string): Promise<AccountPayable[]> {
    const payables = await this.table.where('contactId').equals(contactId).toArray();
    return payables.map(this.stripOfflineMetadata);
  }

  /**
   * Get overdue payables
   */
  async getOverdue(): Promise<AccountPayable[]> {
    const today = new Date().toISOString().split('T')[0];
    const payables = await this.table
      .filter((p) => {
        if (!p.dueDate) return false;
        return (
          p.dueDate < today &&
          (p.status === DebtStatus.ACTIVE || p.status === DebtStatus.PARTIAL)
        );
      })
      .toArray();
    return payables.map(this.stripOfflineMetadata);
  }

  /**
   * Calculate summary from cached data
   */
  async getSummary(): Promise<PayablesSummary> {
    const payables = await this.table.toArray();

    let totalOwed = 0;
    let pendingCount = 0;
    let paidCount = 0;
    let totalPaid = 0;

    for (const payable of payables) {
      if (payable.status === DebtStatus.ACTIVE || payable.status === DebtStatus.PARTIAL) {
        totalOwed += payable.amount - payable.paidAmount;
        pendingCount++;
      } else if (payable.status === DebtStatus.PAID) {
        paidCount++;
      }
      totalPaid += payable.paidAmount;
    }

    return {
      totalOwed,
      pendingCount,
      paidCount,
      totalPaid,
    };
  }

  /**
   * Override applyFilters for payable-specific filtering
   */
  protected override applyFilters(
    items: OfflineEntity<AccountPayable>[],
    filters: Record<string, unknown>
  ): OfflineEntity<AccountPayable>[] {
    return items.filter((item) => {
      // Status filter
      if (filters.status && item.status !== filters.status) {
        return false;
      }

      // Contact filter
      if (filters.contactId && item.contactId !== filters.contactId) {
        return false;
      }

      // Branch filter
      if (filters.branchId && item.branchId !== filters.branchId) {
        return false;
      }

      // Date range filter
      if (filters.startDate && item.dueDate) {
        const startDate = new Date(filters.startDate as string);
        if (new Date(item.dueDate) < startDate) {
          return false;
        }
      }
      if (filters.endDate && item.dueDate) {
        const endDate = new Date(filters.endDate as string);
        if (new Date(item.dueDate) > endDate) {
          return false;
        }
      }

      return true;
    });
  }
}

// ============================================
// SINGLETON INSTANCE
// ============================================

export const payableOfflineService = new PayableOfflineService();
